import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import "@progressive-development/pd-icon/pd-icon";
import { pdIcons } from "@progressive-development/pd-icon";
import { getCurrentFormatLocale } from "@progressive-development/pd-utils";
//#region src/pd-rating/PdRatingStars.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
var MAX_STARS = 5;
var PdRatingStars = class PdRatingStars extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.value = 0;
		this.interactive = false;
		this.allowClear = false;
		this._hover = null;
	}
	static {
		this.styles = css`
    :host {
      display: inline-flex;
      --_star-size: var(--pd-rating-star-size, 1.25rem);
      --_star-col: var(
        --pd-rating-star-col,
        var(--pd-default-warning-col, #f0a417)
      );
      --_star-empty-col: var(
        --pd-rating-star-empty-col,
        var(--pd-default-light-col, #d0d0d0)
      );
    }

    .stars {
      display: inline-flex;
      align-items: center;
    }

    pd-icon {
      --pd-icon-size: var(--_star-size);
      --pd-icon-col: var(--_star-empty-col);
      --pd-icon-col-active: var(--_star-col);
      /* Hover-Farbwechsel des Icons unterdrücken — der Zustand kommt
       * ausschließlich über fillLevel. */
      --pd-icon-col-hover: var(--_star-empty-col);
      padding: 0;
    }

    button {
      font: inherit;
      border: none;
      background: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      display: inline-flex;
      border-radius: var(--pd-radius-sm, 2px);
    }

    button:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col, currentColor);
      outline-offset: 1px;
    }
  `;
	}
	render() {
		return this.interactive ? this._renderPicker() : this._renderDisplay();
	}
	_renderDisplay() {
		const value = this._clamp(this.value);
		return html`
      <span
        class="stars"
        role="img"
        aria-label="${msg(str`Bewertung: ${this._formatValue(value)} von 5 Sternen`, { id: "pd.rating.stars.label" })}"
      >
        ${this._starIndexes().map((i) => html`
            <pd-icon
              icon="${pdIcons.ICON_STAR}"
              fillLevel="${this._fillFor(value, i)}"
              aria-hidden="true"
            ></pd-icon>
          `)}
      </span>
    `;
	}
	/** Fill of star i (1-based) for a decimal value: 100 / partial / 0. */
	_fillFor(value, i) {
		return Math.round(Math.max(0, Math.min(1, value - (i - 1))) * 100);
	}
	_renderPicker() {
		const preview = this._hover ?? this._clamp(this.value);
		return html`
      <span
        class="stars"
        role="radiogroup"
        aria-label="${msg("Bewertung auswählen", { id: "pd.rating.stars.pickerLabel" })}"
        @mouseleave="${() => this._hover = null}"
        @keydown="${this._onKeyDown}"
      >
        ${this._starIndexes().map((i) => html`
            <button
              role="radio"
              aria-checked="${this.value === i}"
              aria-label="${msg(str`${i} von 5 Sternen`, { id: "pd.rating.stars.option" })}"
              tabindex="${this._tabIndexFor(i)}"
              @click="${() => this._select(i)}"
              @mouseenter="${() => this._hover = i}"
            >
              <pd-icon
                icon="${pdIcons.ICON_STAR}"
                fillLevel="${preview >= i ? 100 : 0}"
                aria-hidden="true"
              ></pd-icon>
            </button>
          `)}
      </span>
    `;
	}
	/** Roving tabindex: selected star, or the first when none is selected. */
	_tabIndexFor(i) {
		return i === (Math.round(this._clamp(this.value)) || 1) ? 0 : -1;
	}
	_select(i) {
		const next = this.allowClear && this.value === i ? 0 : i;
		this._applyValue(next);
	}
	_onKeyDown(e) {
		const current = Math.round(this._clamp(this.value));
		let next = null;
		if (e.key === "ArrowRight" || e.key === "ArrowUp") next = Math.min(MAX_STARS, current + 1);
		else if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = Math.max(this.allowClear ? 0 : 1, current - 1);
		if (next === null || next === current) {
			if (next !== null) e.preventDefault();
			return;
		}
		e.preventDefault();
		this._applyValue(next);
		this.updateComplete.then(() => {
			this.shadowRoot?.querySelector("button[tabindex=\"0\"]")?.focus();
		});
	}
	_applyValue(next) {
		if (next === this.value) return;
		this.value = next;
		this._hover = null;
		this.dispatchEvent(new CustomEvent("pd-rating-stars-change", {
			detail: { value: next === 0 ? null : next },
			bubbles: true,
			composed: true
		}));
	}
	_starIndexes() {
		return Array.from({ length: MAX_STARS }, (_, i) => i + 1);
	}
	_clamp(value) {
		if (!Number.isFinite(value)) return 0;
		return Math.max(0, Math.min(MAX_STARS, value));
	}
	_formatValue(value) {
		return value.toLocaleString(getCurrentFormatLocale(), { maximumFractionDigits: 1 });
	}
};
__decorate([property({ type: Number })], PdRatingStars.prototype, "value", void 0);
__decorate([property({ type: Boolean })], PdRatingStars.prototype, "interactive", void 0);
__decorate([property({ type: Boolean })], PdRatingStars.prototype, "allowClear", void 0);
__decorate([state()], PdRatingStars.prototype, "_hover", void 0);
PdRatingStars = __decorate([localized()], PdRatingStars);
//#endregion
export { PdRatingStars };
